// =============================================================================
// src/hooks/useBookings.ts
// =============================================================================

import { supabase } from "@cl/supabase";
import { useAuth } from "@h/useCtx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateBookings } from "@u/queryInvalidation";
import type { BookingStatus, BookingWithTour, ProposeRescheduleInput } from "@/types/booking.types";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const UPDATE_BOOKING_STATUS_FN = "update-booking-status";

export function useBookings(statusFilter?: BookingStatus | BookingStatus[]) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const qc = useQueryClient();

  const queryKey = ["bookings", user?.id, statusFilter];

  const { data: bookings = [], isLoading } = useQuery<BookingWithTour[]>({
    queryKey,
    queryFn: async () => {
      if (!user) return [];
      let q = supabase
        .from("bookings")
        .select("*, tour:tours(title, short_id)")
        .eq("owner_id", user.id)
        .order("start_at", { ascending: true });

      if (statusFilter) {
        const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
        q = q.in("status", statuses);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as BookingWithTour[];
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });

  const confirmMutation = useMutation({
    mutationFn: async ({
      bookingId,
      ownerNotes,
    }: {
      bookingId: string;
      ownerNotes?: string;
    }) => {
      const { error } = await supabase.functions.invoke(UPDATE_BOOKING_STATUS_FN, {
        body: { booking_id: bookingId, action: "confirm", owner_notes: ownerNotes },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateBookings(qc, user?.id);
      toast.success(t("booking_confirm_success"));
    },
    onError: () => toast.error(t("booking_confirm_error")),
  });

  const cancelMutation = useMutation({
    mutationFn: async ({
      bookingId,
      ownerNotes,
    }: {
      bookingId: string;
      ownerNotes?: string;
    }) => {
      const { error } = await supabase.functions.invoke(UPDATE_BOOKING_STATUS_FN, {
        body: { booking_id: bookingId, action: "cancel", owner_notes: ownerNotes },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateBookings(qc, user?.id);
      toast.success(t("booking_cancel_success"));
    },
    onError: () => toast.error(t("booking_cancel_error")),
  });

  const proposeRescheduleMutation = useMutation({
    mutationFn: async (input: ProposeRescheduleInput) => {
      const { error } = await supabase.functions.invoke(UPDATE_BOOKING_STATUS_FN, {
        body: {
          booking_id: input.booking_id,
          action: "propose_reschedule",
          proposed_start_at: input.proposed_start_at,
          proposed_end_at: input.proposed_end_at,
          notes: input.notes,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateBookings(qc, user?.id);
      toast.success(t("proposal_sent_visitor"));
    },
    onError: () => toast.error(t("proposal_send_error")),
  });

  const deleteMutation = useMutation({
    mutationFn: async (bookingId: string) => {
      const { error } = await supabase
        .from("bookings")
        .delete()
        .eq("id", bookingId)
        .eq("owner_id", user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateBookings(qc, user?.id);
      toast.success(t("booking_delete_success"));
    },
    onError: () => toast.error(t("booking_delete_error")),
  });

  return {
    bookings,
    isLoading,
    confirm: confirmMutation.mutateAsync,
    cancel: cancelMutation.mutateAsync,
    proposeReschedule: proposeRescheduleMutation.mutateAsync,
    deleteBooking: deleteMutation.mutateAsync,
    isConfirming: confirmMutation.isPending,
    isCancelling: cancelMutation.isPending,
    isProposing: proposeRescheduleMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

export function useBookingCounts() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["bookings-count-pending", user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count } = await supabase
        .from("bookings")
        .select("id", { count: "exact", head: true })
        .eq("owner_id", user.id)
        .eq("status", "pending");
      return count ?? 0;
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });
}
